import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';

const ServicesGrid = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const services = [
    {
      id: 'concrete',
      number: '01',
      title: 'Concrete',
      description: 'Professional concrete pouring, finishing, and installation for lasting foundations',
      image: 'https://public.readdy.ai/ai/img_res/ffede3c2cda3e0d276882a048de85e25.jpg',
    },
    {
      id: 'patio',
      number: '02',
      title: 'Patio',
      description: 'Custom patio designs creating beautiful outdoor living spaces for your home',
      image: 'https://readdy.ai/api/search-image?query=Beautiful%20custom%20patio%20installation%20in%20progress%2C%20workers%20laying%20patio%20stones%20and%20pavers%2C%20outdoor%20living%20space%20construction%2C%20professional%20craftsmanship%20with%20simple%20neutral%20background%20highlighting%20the%20patio%20work%2C%20natural%20daylight%2C%20clean%20and%20organized%20construction%20site&width=600&height=750&seq=service-patio-02&orientation=portrait',
    },
    {
      id: 'pavers',
      number: '03',
      title: 'Pavers',
      description: 'Expert paver installation for driveways, walkways, and decorative features',
      image: 'https://readdy.ai/api/search-image?query=Professional%20paver%20installation%20work%20in%20progress%2C%20workers%20carefully%20placing%20interlocking%20pavers%20in%20geometric%20patterns%2C%20driveway%20and%20walkway%20construction%2C%20detailed%20craftsmanship%20with%20simple%20neutral%20background%20highlighting%20the%20paver%20work%2C%20bright%20natural%20lighting%2C%20precise%20and%20clean%20installation&width=600&height=750&seq=service-pavers-03&orientation=portrait',
    },
    {
      id: 'drywall',
      number: '04',
      title: 'Drywall',
      description: 'Professional drywall installation, repair, and finishing for perfect walls',
      image: 'https://readdy.ai/api/search-image?query=Professional%20drywall%20installation%20and%20finishing%20work%2C%20construction%20worker%20applying%20drywall%20compound%20and%20smoothing%20walls%2C%20interior%20construction%20site%2C%20skilled%20craftsmanship%20with%20simple%20neutral%20background%20highlighting%20the%20drywall%20work%2C%20even%20lighting%2C%20clean%20and%20professional%20workspace&width=600&height=750&seq=service-drywall-04&orientation=portrait',
    },
  ];

  return (
    <section className="bg-brand-light py-24 lg:py-32" ref={ref}>
      <div className="max-w-7xl mx-auto px-6 lg:px-16">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="text-brand-green text-sm font-bold uppercase tracking-widest mb-4">OUR EXPERTISE</p>
          <h2 className="text-5xl lg:text-6xl font-black text-gray-900 mb-4">Premium Services</h2>
          <p className="font-serif italic text-3xl lg:text-4xl text-gray-700">Crafted to Perfection</p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {services.map((service, index) => (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <Link to={`/service/${service.id}`} className="group block">
                <div className="relative h-[450px] rounded-3xl overflow-hidden cursor-pointer">
                  {/* Image */}
                  <div className="absolute inset-0">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
                    />
                  </div>

                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent group-hover:from-black/70 transition-all duration-300"></div>

                  {/* Content */}
                  <div className="absolute inset-0 p-8 flex flex-col justify-between">
                    {/* Number */}
                    <div className="text-brand-green text-7xl font-black opacity-80">{service.number}</div>

                    {/* Bottom Content */}
                    <div>
                      <h3 className="text-white text-3xl font-bold mb-3">{service.title}</h3>
                      <p className="text-white/80 text-sm leading-relaxed mb-4">{service.description}</p>
                    </div>
                  </div>

                  {/* Arrow Button */}
                  <div className="absolute bottom-8 right-8 w-14 h-14 flex items-center justify-center border-2 border-white rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300">
                    <i className="ri-arrow-right-line text-white text-xl"></i>
                  </div>

                  {/* More Info Button - Center on Hover */}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300">
                    <div className="bg-white text-black px-8 py-3 rounded-full font-semibold text-sm whitespace-nowrap">
                      MORE INFO
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesGrid;